import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import BusinessList from '../components/business/BusinessList';
import { businessService } from '../services/businessService';
import { getRecentBusinesses } from '../utils/recentBusinesses';

/**
 * Home page with business discovery
 */
const HomePage = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const [recentBusinesses, setRecentBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBusinesses = async () => {
    setLoading(true);
    setError(null);
    try {
      const [allBiz, featuredBiz] = await Promise.all([
        businessService.getAllBusinesses(),
        businessService.getFeaturedBusinesses(6),
      ]);
      setBusinesses(allBiz);
      setFeaturedBusinesses(featuredBiz);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinesses();
    // Load recent businesses from cookie
    const recent = getRecentBusinesses();
    setRecentBusinesses(recent);
  }, []);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.trim()) {
      try {
        const results = await businessService.searchBusinesses(query);
        setBusinesses(results);
      } catch (err) {
        console.error('Search error:', err);
      }
    } else {
      // Reset to all businesses
      fetchBusinesses();
    }
  };

  const filteredBusinesses = searchQuery.trim() 
    ? businesses 
    : businesses;

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: { xs: 2, sm: 4 } }}>
        {/* Header */}
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom
          sx={{ 
            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
            fontWeight: 'bold',
          }}
        >
          {t('home.title')}
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          paragraph
          sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
        >
          {t('home.subtitle')}
        </Typography>

        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder={t('home.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ mb: 4, maxWidth: 600 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Recently Viewed */}
        {!searchQuery && recentBusinesses.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <BusinessList
              businesses={recentBusinesses}
              loading={false}
              error={null}
              title={t('home.recentlyViewed')}
            />
          </Box>
        )}

        {/* Featured Businesses */}
        {!searchQuery && featuredBusinesses.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <BusinessList
              businesses={featuredBusinesses}
              loading={loading}
              error={error}
              onRetry={fetchBusinesses}
              title={t('home.featuredBusinesses')}
            />
          </Box>
        )}

        <Divider sx={{ my: 4 }} />

        {/* All Businesses */}
        <BusinessList
          businesses={filteredBusinesses}
          loading={loading}
          error={error}
          onRetry={fetchBusinesses}
          title={searchQuery ? `Search results for "${searchQuery}"` : t('home.allBusinesses')}
        />
      </Box>
    </Container>
  );
};

export default HomePage;
